import { FC, useEffect, useMemo, useState } from 'react';
import { Center, IconButton, Input, Switch, Tooltip, useDisclosure, useToast, VStack, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Button, Select, Text } from '@chakra-ui/react';
import { MdAdd, MdContentCopy, MdRefresh } from 'react-icons/md';
import { CampaignLinkDTO, createCampaignLink, deleteCampaignLink, listCampaignLinks, updateCampaignLink } from '@/services/campaign-links-service';
import { ColumnDef } from '@tanstack/react-table';
import { PageHeading, Panel, Table, TableRowActions } from '@bemap/ui';
import { useTranslation } from 'react-i18next';
import { useFunnels, SalesFunnel } from '@bemap/crm-microfrontends';
import { getFunnels } from '@/services/crm-service';

const CampaignLinksPage: FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<CampaignLinkDTO[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newLabel, setNewLabel] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newFunnelId, setNewFunnelId] = useState<string>('');
  const [funnelUpdatingMap, setFunnelUpdatingMap] = useState<Record<number, boolean>>({});

  const fetchFunnels = async () => {
    const res = await getFunnels();
    return ((res as any).message || []) as SalesFunnel[];
  };

  const { funnels, loading: funnelsLoading } = useFunnels(fetchFunnels);

  const filtered = items;

  const funnelOptions = useMemo(() => [
    { value: '', label: t('general.none') || 'None' },
    ...funnels.map((funnel) => ({ value: funnel.id.toString(), label: funnel.name })),
  ], [funnels, t]);

  // removed unused getFunnelName

  async function load() {
    setIsLoading(true);
    try {
      const res = await listCampaignLinks();
      setItems((res as any).message || []);
    } catch (e) {
      setItems([]);
      toast({ status: 'error', title: t('general.error') || 'Error' });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function onCreateConfirm() {
    try {
      setIsCreating(true);
      const res = await createCampaignLink({
        label: newLabel || null,
        sales_funnel_id: newFunnelId ? Number(newFunnelId) : null,
      });
      const created = (res as any).message;
      setItems(prev => [created, ...prev]);
      setNewLabel('');
      setNewFunnelId('');
      onClose();
      toast({ status: 'success', title: t('general.created') || 'Created' });
    } catch (e) {
      toast({ status: 'error', title: t('general.error') || 'Error' });
    } finally {
      setIsCreating(false);
    }
  }

  const handleFunnelChange = async (item: CampaignLinkDTO, value: string) => {
    const parsed = value ? Number(value) : null;
    setFunnelUpdatingMap(prev => ({ ...prev, [item.id]: true }));
    try {
      const res = await updateCampaignLink(item.id, { sales_funnel_id: parsed });
      const updated = (res as any).message;
      setItems(prev => prev.map(x => x.id === item.id ? updated : x));
    } catch (e) {
      toast({ status: 'error', title: t('general.error') || 'Error' });
    } finally {
      setFunnelUpdatingMap(prev => ({ ...prev, [item.id]: false }));
    }
  };

  const onToggleActive = async (item: CampaignLinkDTO) => {
    try {
      const res = await updateCampaignLink(item.id, { is_active: !item.is_active });
      const updated = (res as any).message;
      setItems(prev => prev.map(x => x.id === item.id ? updated : x));
    } catch (e) {
      toast({ status: 'error', title: t('general.error') || 'Error' });
    }
  };

  const onDelete = async (id: number) => {
    try {
      await deleteCampaignLink(id);
      setItems(prev => prev.filter(x => x.id !== id));
      toast({ status: 'success', title: t('general.deleted') || 'Deleted' });
    } catch (e) {
      toast({ status: 'error', title: t('general.error') || 'Error' });
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ status: 'success', title: t('general.copied') || 'Copied' });
    } catch { }
  };

  const columns = useMemo<ColumnDef<CampaignLinkDTO, any>[]>(() => [
    {
      accessorKey: 'label',
      id: 'label',
      header: t('general.label') || 'Label',
      cell: ({ getValue }) => getValue() as string,
    },
    {
      accessorKey: 'sales_funnel_id',
      id: 'sales_funnel_id',
      header: t('crm.funnel') || 'Funnel',
      cell: ({ row }) => {
        const item = row.original;
        if (funnelsLoading) {
          return <Text>{t('general.loading') || 'Loading...'}</Text>;
        }
        return (
          <Select
            size="sm"
            value={item.sales_funnel_id ? item.sales_funnel_id.toString() : ''}
            onChange={(e) => handleFunnelChange(item, e.target.value)}
            isDisabled={!!funnelUpdatingMap[item.id]}
          >
            {funnelOptions.map((option) => (
              <option key={option.value || 'none'} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        );
      },
    },
    {
      accessorKey: 'code',
      id: 'code',
      header: t('general.code') || 'Code',
      cell: ({ getValue }) => getValue() as string,
    },
    {
      accessorKey: 'link',
      id: 'link',
      header: t('general.link') || 'Link',
      enableColumnFilter: false,
      cell: ({ row }) => {
        const item = row.original;
        const path = `https://assessments.bemap.com.br/campaign/${item.code}`;
        return (
          <Tooltip label={t('actions.copy') || 'Copy'}>
            <IconButton aria-label="copy" icon={<MdContentCopy />} onClick={() => copy(path)} />
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'is_active',
      id: 'active',
      header: t('general.active') || 'Active',
      cell: ({ row }) => {
        const item = row.original;
        return (
          <Switch isChecked={!!item.is_active} onChange={() => onToggleActive(item)} />
        );
      },
    },
    {
      accessorKey: 'actions',
      id: 'actions',
      header: t('table.actions') || 'Actions',
      enableColumnFilter: false,
      cell: ({ row }) => (
        <TableRowActions deleteAction={() => onDelete(row.original.id)} />
      ),
    },
  ], [t, items]);

  const actions = [
    {
      label: t('actions.new') || 'New',
      icon: <MdAdd />,
      colorScheme: 'blue',
      onClick: onOpen,
    },
    {
      label: t('actions.refresh') || 'Refresh',
      icon: <MdRefresh />,
      colorScheme: 'blue',
      variant: 'outline',
      onClick: load,
    },
  ];

  if (isLoading) {
    return (
      <Center h="400px" />
    );
  }

  return (
    <VStack w="100%" gap={4} align="stretch">
      <PageHeading title={t('analyst.campaign_links') || 'Campaign Links'} actions={actions} />
      <Panel>
        <VStack align="stretch" gap={3}>
          <Table columns={columns} data={filtered} />
        </VStack>
      </Panel>
      <Modal isOpen={isOpen} onClose={() => { if (!isCreating) { onClose(); setNewLabel(''); } }} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('actions.new') || 'New'}</ModalHeader>
          <ModalCloseButton disabled={isCreating} />
          <ModalBody>
            <VStack align="stretch" gap={3}>
              <Input
                placeholder={t('general.label') || 'Label'}
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
              />
              <Select
                value={newFunnelId}
                onChange={(e) => setNewFunnelId(e.target.value)}
                isDisabled={funnelsLoading}
              >
                {funnelOptions.map((option) => (
                  <option key={option.value || 'none'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="outline" onClick={onClose} isDisabled={isCreating}>
              {t('actions.cancel') || 'Cancel'}
            </Button>
            <Button colorScheme="blue" onClick={onCreateConfirm} isLoading={isCreating}>
              {t('actions.save') || 'Save'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default CampaignLinksPage;


